/**
 * 환율 — S3(CloudFront) 정적 파일을 **앱이 직접** 읽는다.
 *
 * BE 를 거치지 않는 이유(2026-08-24 결정): 전 사용자가 부팅마다 읽는 값인데 내용은 하루 한 번만
 * 바뀐다. 서버를 태우면 그 트래픽이 전부 오리진으로 오고, 글로벌로 나가면 왕복 지연까지 붙는다.
 * 발행은 adbe `ExchangeRateScheduler` 가 한다 — 같은 스냅샷으로 `pr_exrt_bydd` 도 함께 채우므로
 * 화면 환율과 시세 계산 환율이 어긋나지 않는다.
 *
 * ⚠ **표시 전용이다.** 무료 공개 피드(open.er-api.com) 기반이라 은행 고시환율과 다르고 SLA 도
 * 없다. 결제·정산에 쓰지 않는다.
 */
import { env } from "../config/env";

/** 알고 있는 파일 포맷 버전. 더 높은 버전이 오면 해석하지 않고 캐시를 쓴다. */
export const FX_SCHEMA_VERSION = 1;

export interface ExchangeRateSnapshot {
  schema: number;
  /** 이 맵의 기준 통화. KRW 가 아니어도 된다 — 교차환율로 임의 통화쌍이 나온다. */
  baseCurrency: string;
  /** 출처가 값을 확정한 시각(ISO). 화면이 "환율 기준" 을 밝힐 때 쓴다. */
  asOf: string;
  baseDate: string;
  source: string;
  /** 통화코드 → base 1단위당 그 통화의 양 */
  rates: Record<string, number>;
}

/** 환율 파일 주소. 오리진이 설정되지 않았으면 null — 호출부가 통화 전환을 끈다. */
export function exchangeRateUrl(): string | null {
  const base = env.VITE_FX_BASE_URL?.trim();
  if (!base) return null;
  return `${base.replace(/\/+$/, "")}/fx/latest.json`;
}

/**
 * 최신 환율 스냅샷.
 *
 * ⚠ `credentials` 를 붙이지 않는다 — 공개 CDN 파일이고, 자격증명을 실으면 CORS 가
 * `Access-Control-Allow-Origin: *` 와 충돌해 요청 자체가 막힌다.
 */
export async function fetchExchangeRates(): Promise<ExchangeRateSnapshot> {
  const url = exchangeRateUrl();
  if (!url) throw new Error("환율 오리진(VITE_FX_BASE_URL)이 설정되지 않았습니다");

  const res = await fetch(url, { credentials: "omit" });
  if (!res.ok) throw new Error(`환율 조회 실패: ${res.status}`);

  const body = (await res.json()) as ExchangeRateSnapshot;
  if (!body?.rates || typeof body.rates !== "object" || !body.baseCurrency) {
    throw new Error("환율 응답 형식이 올바르지 않습니다");
  }
  if (body.schema > FX_SCHEMA_VERSION) {
    // 앞선 버전은 필드 뜻이 달라졌을 수 있다 — 넘겨짚어 환산하느니 안 쓴다.
    throw new Error(`알 수 없는 환율 스키마: ${body.schema}`);
  }
  return body;
}

/**
 * 교차환율 — base 가 무엇이든 A→B.
 * 둘 중 하나라도 없으면 null 이다. 1 로 떨어뜨리면 없는 금액을 만들어낸다.
 */
export function crossRate(
  snapshot: ExchangeRateSnapshot | undefined,
  from: string,
  to: string,
): number | null {
  if (!snapshot) return null;
  if (from === to) return 1;
  const f = snapshot.rates[from];
  const t = snapshot.rates[to];
  if (!f || !t) return null;
  return t / f;
}
